import React from 'react';
import { observer } from 'mobx-react';
import { Table, Modal, Statistic, Select, Empty, Card, Progress } from 'antd';
import { Line } from '@ant-design/plots';
import { ColumnProps } from 'antd/es/table';
import {
    useFeedbackNPSStore,
    useFeedbackStore,
    useApplicationSelectionStore,
} from '../../contexts/mobx';
import { Feedback, SearchParams } from '../../stores/feedbacks';
import {
    AnalyticsCompareOptions,
    ANALYTICS_DEFAULT_COMPARE_OPTION,
    FeedbackTypes,
    FeedbackTypeTagVariants,
} from '../../utils/const';
import { useSearchParams } from '../../utils/use-search-params';
import { AnalyticsCompareDates } from '../../components/analytics-compare-dates';
import { AnalyticsSearchOptions } from '../../components/analytics-search-options';
import { ExportButton } from '../../components/action-buttons';
import { NotificationManager } from '../../components/notification-manager';
import { PageTopBar } from '../../components/page-top-bar';
import { PaginationBar } from '../../components/pagination/pagination-bar';
import { Tag } from '../../components/tag';
import style from './style.module.scss';
import moment from 'moment';
import { downloadCSVFile } from '../../utils/csv';
import { LINE_CONFIG, NPS } from '../../helpers/analytics-helpers';
import { Text } from '../../utils/text';
import * as Colors from '../../styles/colors';
import {
    ApplicationSelection,
    ApplicationTargetEnum,
} from '../../types/app-selection';

const renderUserName = (_: unknown, record: Feedback) => {
    return (
        <div>
            {record.user.firstName} {record.user.lastName}
        </div>
    );
};

const renderScore = (_: unknown, record: Feedback) => {
    return (
        <div>
            <Tag
                variant={FeedbackTypeTagVariants[record.score as FeedbackTypes]}
            >
                {record.score}
            </Tag>
        </div>
    );
};

const renderDescription = (_: unknown, record: Feedback) => {
    return <div>{record.description}</div>;
};

const renderFeatureScored = (_: unknown, record: Feedback) => {
    return <div>{record.featureScored}</div>;
};

const renderSelectedStore = (_: unknown, record: Feedback) => {
    return <div>{record.selectedStore}</div>;
};

const renderCreatedDate = (_: unknown, record: Feedback) => {
    return (
        <div>
            <div>{record.createdAt?.format('ll')}</div>
        </div>
    );
};

export const FeedbacksPage = observer(() => {
    const feedbackStore = useFeedbackStore();
    const npsStore = useFeedbackNPSStore();
    const applicationSelectionStore = useApplicationSelectionStore();
    const search = useSearchParams<SearchParams>();
    const [isModalVisible, setIsModalVisible] = React.useState(false);
    const [
        selectedFeedback,
        setSelectedFeedback,
    ] = React.useState<Feedback | null>(null);

    const [dateRange] = React.useState<number>(28);
    const [isComparing, setCompare] = React.useState<boolean>(false);
    const [searchPeriod, setSearchPeriod] = React.useState<
        AnalyticsCompareOptions
    >(ANALYTICS_DEFAULT_COMPARE_OPTION);

    const currentNpsPerDayArray: NPS[] = [];
    const compareNpsPerDayArray: NPS[] = [];

    npsStore.npsList?.currentPeriod?.npsDay.map((r) => {
        const nps: NPS = {
            value: Number(r.npsOfTheDay),
            date: r.date?.format('ll'),
        };
        currentNpsPerDayArray.push(nps);
    });

    const data = currentNpsPerDayArray;

    npsStore.npsList?.comparePeriod?.npsDay?.map((r) => {
        const nps: NPS = {
            value: Number(r.npsOfTheDay),
            date: r.date?.format('ll'),
        };
        compareNpsPerDayArray.push(nps);
    });

    const compareData = compareNpsPerDayArray;

    const npsPeriodScore = Number(
        npsStore.npsList?.currentPeriod?.npsPeriod.npsOfThePeriod
    );
    const compareNpsPeriodScore = Number(
        npsStore.npsList?.comparePeriod?.npsPeriod?.npsOfThePeriod
    );

    const fetchParams = React.useMemo(
        () => ({
            ...search.params,
            appTarget: applicationSelectionStore.appTarget,
        }),
        [search.params, applicationSelectionStore.appTarget]
    );

    React.useEffect(() => {
        feedbackStore.fetchList(fetchParams);
    }, [feedbackStore, fetchParams]);

    React.useEffect(() => {
        npsStore.fetchNPS(fetchParams);
    }, [npsStore, fetchParams]);

    const isAllRegions =
        applicationSelectionStore.selectedApplication ===
        ApplicationSelection.ALL_REGIONS;

    const columns = React.useMemo<ColumnProps<Feedback>[]>(() => {
        const baseColumns: ColumnProps<Feedback>[] = [
            {
                title: Text.feedbacksPage.table.userName,
                key: 'userName',
                render: renderUserName,
            },
            {
                title: Text.feedbacksPage.table.score,
                key: 'score',
                render: renderScore,
            },
            {
                title: Text.feedbacksPage.table.description,
                key: 'description',
                render: renderDescription,
            },
            {
                title: Text.feedbacksPage.table.featuredScore,
                key: 'featureScored',
                render: renderFeatureScored,
            },
            {
                title: Text.feedbacksPage.table.selectedStore,
                key: 'selectedStore',
                render: renderSelectedStore,
            },
            {
                title: Text.feedbacksPage.table.createdDate,
                key: 'createdDate',
                render: renderCreatedDate,
            },
        ];

        if (isAllRegions) {
            baseColumns.push({
                title: 'App',
                key: 'appTarget',
                width: 100,
                render: (_: unknown, record: Feedback) =>
                    record.appTarget === ApplicationTargetEnum.QC
                        ? 'couche_tard'
                        : 'circle_k',
            });
        }

        return baseColumns;
    }, [isAllRegions]);

    const downloadCsv = async () => {
        try {
            const { startDate, endDate, featureScored } = search.params;
            const featured = await feedbackStore.fetchFeedbackForCSV({
                startDate,
                endDate,
                featureScored,
                appTarget: applicationSelectionStore.appTarget,
            });
            if (!featured) {
                NotificationManager.showError('No feedback data to export');
                return;
            }
            const csvData = featured.map((feedback) => ({
                'Full Name': `${feedback.user.firstName} ${feedback.user.lastName}`,
                Score: feedback.score,
                Description: feedback.description,
                'Feature Scored': feedback.featureScored,
                'Selected Store': feedback.selectedStore
                    ? feedback.selectedStore
                    : 'Unknown',
                ...(isAllRegions
                    ? {
                          App:
                              feedback.appTarget === ApplicationTargetEnum.QC
                                  ? 'couche_tard'
                                  : 'circle_k',
                      }
                    : {}),
                'Created Date': feedback.createdAt?.format('YYYY-MM-DD HH:MM'),
            }));
            return downloadCSVFile(csvData, 'feedbacks');
        } catch (error) {
            NotificationManager.showError('Error during exportation of file');
        }
    };

    const compare = async (e: boolean) => {
        if (e) {
            search.setParams({
                compareStartDate: moment()
                    .subtract(2 * dateRange, 'days')
                    .toISOString(),
                compareEndDate: moment()
                    .subtract(dateRange, 'days')
                    .toISOString(),
                page: 0,
            });
        } else {
            search.setParams({
                compareStartDate: undefined,
                compareEndDate: undefined,
                page: 0,
            });
            setSearchPeriod('preceding');
        }
        setCompare(e);
    };

    const changeSearchPeriod = async (
        selectedPeriod: AnalyticsCompareOptions
    ) => {
        setSearchPeriod(selectedPeriod);

        if (isComparing) {
            search.setParams({
                compareStartDate:
                    selectedPeriod === 'preceding'
                        ? moment(search.params.startDate)
                              .subtract(2 * dateRange, 'days')
                              .toISOString()
                        : moment(search.params.startDate)
                              .subtract(1, 'year')
                              .toISOString(),
                compareEndDate:
                    selectedPeriod === 'preceding'
                        ? moment(search.params.startDate)
                              .subtract(dateRange, 'days')
                              .toISOString()
                        : moment(search.params.endDate)
                              .subtract(1, 'year')
                              .toISOString(),
                page: 0,
            });
        }
    };

    return (
        <>
            <PageTopBar title="Feedbacks" />

            <div className={`content_with_margins ${style.cards_container}`}>
                <Card className={style.length_limited}>
                    <AnalyticsSearchOptions
                        // No period lists every feedback, so the range has
                        // to be clearable.
                        allowClear
                        onCompareChange={compare}
                        compareEnabled={isComparing}
                        updateParams={(dates) => {
                            if (!dates) {
                                // A comparison needs a period to sit before,
                                // so clearing the period turns Compare off.
                                search.setParams({
                                    startDate: undefined,
                                    endDate: undefined,
                                    compareStartDate: undefined,
                                    compareEndDate: undefined,
                                    page: 0,
                                });
                                setSearchPeriod('preceding');
                                setCompare(false);
                                return;
                            }
                            search.setParams({
                                startDate: dates[0]?.toISOString(),
                                endDate: dates[1]?.toISOString(),
                                page: 0,
                            });
                        }}
                        startDate={search.params.startDate}
                        endDate={search.params.endDate}
                    />
                </Card>
                <Card className={style.length_limited}>
                    <Select
                        className={style.select_filter}
                        placeholder="Feature Scored"
                        value={search.params.featureScored}
                        onChange={(e) => search.setParams({ featureScored: e })}
                        allowClear
                    >
                        <Select.Option value="carwash">Car Wash</Select.Option>
                        <Select.Option value="ctdelivery">
                            CT Delivery
                        </Select.Option>
                        <Select.Option value="ctconnect">
                            CT Connecté
                        </Select.Option>
                        <Select.Option value="general">General</Select.Option>
                    </Select>

                    {data?.length &&
                    !isNaN(npsPeriodScore) &&
                    isFinite(npsPeriodScore) ? (
                        <div>
                            <div
                                style={{
                                    textAlign: 'center',
                                    padding: '20px',
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: '14px',
                                        color: Colors.UNKNOWN_TEXT_DARK_BLUE,
                                        marginBottom: '10px',
                                    }}
                                >
                                    NPS Score
                                </div>
                                <Progress
                                    type="dashboard"
                                    percent={
                                        ((npsPeriodScore + 100) / 200) * 100
                                    }
                                    strokeColor={
                                        npsPeriodScore > 35
                                            ? Colors.UNKNOWN_FEEDBACK_SUCCESS
                                            : npsPeriodScore >= 0
                                            ? Colors.UNKNOWN_ANT_ORANGE
                                            : Colors.UNKNOWN_FEEDBACK_ERROR
                                    }
                                    format={() => (
                                        <div>
                                            <div
                                                style={{
                                                    fontSize: '32px',
                                                    fontWeight: 'bold',
                                                    color:
                                                        npsPeriodScore > 35
                                                            ? Colors.UNKNOWN_FEEDBACK_SUCCESS
                                                            : npsPeriodScore >=
                                                              0
                                                            ? Colors.UNKNOWN_ANT_ORANGE
                                                            : Colors.UNKNOWN_FEEDBACK_ERROR,
                                                }}
                                            >
                                                {Math.round(npsPeriodScore)}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: '12px',
                                                    color:
                                                        Colors.UNKNOWN_GRAY_888,
                                                }}
                                            >
                                                NPS
                                            </div>
                                        </div>
                                    )}
                                    width={180}
                                />
                            </div>

                            <div className={style.export_button}>
                                <ExportButton onClick={downloadCsv}>
                                    Export to CSV
                                </ExportButton>
                            </div>
                        </div>
                    ) : (
                        <Statistic
                            title="NPS for this period"
                            value="No Feedback"
                        />
                    )}
                </Card>

                <Card className={style.length_limited}>
                    {data?.length ? (
                        <div>
                            <Line
                                {...LINE_CONFIG(data)}
                                className={style.line}
                            />
                        </div>
                    ) : (
                        <Empty
                            className={style.empty_chart}
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    )}
                </Card>
                {isComparing && (
                    <Card
                        className={style.length_limited}
                        title={
                            <>
                                <AnalyticsCompareDates
                                    compareStartDate={
                                        search.params.compareStartDate
                                    }
                                    compareEndDate={
                                        search.params.compareEndDate
                                    }
                                    endDate={search.params.endDate}
                                    format={'YYYY-MM-DD'}
                                    updateParams={(dates) => {
                                        search.setParams({
                                            compareStartDate: dates?.[0]?.toISOString(),
                                            compareEndDate: dates?.[1]?.toISOString(),
                                            page: 0,
                                        });
                                    }}
                                    changeSearchPeriod={changeSearchPeriod}
                                    searchPeriod={searchPeriod}
                                />
                                {isComparing && (
                                    <>
                                        {compareData.length &&
                                        !isNaN(compareNpsPeriodScore) &&
                                        isFinite(compareNpsPeriodScore) ? (
                                            <div
                                                style={{
                                                    textAlign: 'center',
                                                    padding: '20px',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        fontSize: '14px',
                                                        color:
                                                            Colors.UNKNOWN_TEXT_DARK_BLUE,
                                                        marginBottom: '10px',
                                                    }}
                                                >
                                                    Compare NPS Score
                                                </div>
                                                <Progress
                                                    type="dashboard"
                                                    percent={
                                                        ((compareNpsPeriodScore +
                                                            100) /
                                                            200) *
                                                        100
                                                    }
                                                    strokeColor={
                                                        compareNpsPeriodScore >
                                                        35
                                                            ? Colors.UNKNOWN_FEEDBACK_SUCCESS
                                                            : compareNpsPeriodScore >=
                                                              0
                                                            ? Colors.UNKNOWN_ANT_ORANGE
                                                            : Colors.UNKNOWN_FEEDBACK_ERROR
                                                    }
                                                    format={() => (
                                                        <div>
                                                            <div
                                                                style={{
                                                                    fontSize:
                                                                        '32px',
                                                                    fontWeight:
                                                                        'bold',
                                                                    color:
                                                                        compareNpsPeriodScore >
                                                                        35
                                                                            ? Colors.UNKNOWN_FEEDBACK_SUCCESS
                                                                            : compareNpsPeriodScore >=
                                                                              0
                                                                            ? Colors.UNKNOWN_ANT_ORANGE
                                                                            : Colors.UNKNOWN_FEEDBACK_ERROR,
                                                                }}
                                                            >
                                                                {Math.round(
                                                                    compareNpsPeriodScore
                                                                )}
                                                            </div>
                                                            <div
                                                                style={{
                                                                    fontSize:
                                                                        '12px',
                                                                    color:
                                                                        Colors.UNKNOWN_GRAY_888,
                                                                }}
                                                            >
                                                                NPS
                                                            </div>
                                                        </div>
                                                    )}
                                                    width={180}
                                                />
                                            </div>
                                        ) : (
                                            <Statistic
                                                title="Comparaison NPS for this period"
                                                value="No NPS"
                                                className={style.stats}
                                            />
                                        )}
                                    </>
                                )}
                            </>
                        }
                    >
                        {data?.length ? (
                            <div>
                                {compareData.length ? (
                                    <Line
                                        {...LINE_CONFIG(compareData)}
                                        className={style.line}
                                    />
                                ) : (
                                    <Empty
                                        className={style.empty_chart}
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description={
                                            <span>No NPS to compare</span>
                                        }
                                    />
                                )}
                            </div>
                        ) : (
                            <Empty
                                className={style.empty_chart}
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        )}
                    </Card>
                )}
                <Card className={style.length_limited}>
                    <PaginationBar
                        pagination={{
                            page: search.params.page,
                            pageSize: search.params.perPage,
                            total: feedbackStore.list?.total,
                            onPageChange: (page) => search.setParams({ page }),
                            onPageSizeChange: (perPage) =>
                                search.setParams({ perPage, page: 0 }),
                        }}
                    />
                    <Table<Feedback>
                        className={'clickable_table_body_rows'}
                        rowKey="id"
                        columns={columns}
                        dataSource={feedbackStore.list?.results}
                        loading={feedbackStore.fetchingCount > 0}
                        onRow={(record) => ({
                            onClick: () => {
                                setSelectedFeedback(record);
                                setIsModalVisible(true);
                            },
                        })}
                        pagination={false}
                    />
                </Card>
            </div>

            <Modal
                title="Feedback"
                open={isModalVisible}
                onOk={() => setIsModalVisible(false)}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
            >
                <p>
                    <b>{Text.feedbacksPage.modal.user}</b>
                    <span>{selectedFeedback?.userId}</span>
                </p>
                <p>
                    <b>{Text.feedbacksPage.modal.username}</b>
                    <span>
                        {selectedFeedback?.user?.firstName}{' '}
                        {selectedFeedback?.user?.lastName}
                    </span>
                </p>
                <p>
                    <b>{Text.feedbacksPage.modal.email}</b>
                    <span>{selectedFeedback?.user?.email}</span>
                </p>
                <p>
                    <b>{Text.feedbacksPage.modal.score}</b>
                    <span>{selectedFeedback?.score}</span>
                </p>
                <p>
                    <b>{Text.feedbacksPage.modal.category}</b>
                    <span>{selectedFeedback?.category}</span>
                </p>
                <p>
                    <b>{Text.feedbacksPage.modal.featureScored}</b>
                    <span>{selectedFeedback?.featureScored}</span>
                </p>
                <p>
                    <b>{Text.feedbacksPage.modal.selectedStore}</b>
                    <span>{selectedFeedback?.selectedStore}</span>
                </p>
                <p>
                    <b>{Text.feedbacksPage.modal.description}</b>
                    <span>{selectedFeedback?.description}</span>
                </p>
                <p>
                    <b>{Text.feedbacksPage.modal.lang}</b>
                    <span>{selectedFeedback?.user?.language}</span>
                </p>
                <p>
                    <b>{Text.feedbacksPage.modal.birthday}</b>
                    <span>
                        {selectedFeedback?.user?.birthday?.format('ll')}
                    </span>
                </p>
                <p>
                    <b>{Text.feedbacksPage.modal.gender}</b>
                    <span>{selectedFeedback?.user?.gender}</span>
                </p>
                <p>
                    <b>{Text.feedbacksPage.modal.phoneNumber}</b>
                    <span>{selectedFeedback?.user?.phoneNumber}</span>
                </p>
                <p>
                    <b>{Text.feedbacksPage.modal.postalCode}</b>
                    <span>{selectedFeedback?.user?.postalCode}</span>
                </p>
                <p>
                    <b>{Text.feedbacksPage.modal.newsletter_receiver}</b>
                    <span>
                        {selectedFeedback?.user?.newsletter_receiver
                            ? 'Yes'
                            : 'No'}
                    </span>
                </p>
                <p>
                    <b>{Text.feedbacksPage.modal.updateAt}</b>
                    <span>
                        {selectedFeedback?.updateAt?.format('ll') ?? 'Never'}
                    </span>
                </p>
                <p>
                    <b>{Text.feedbacksPage.modal.createdAt}</b>
                    <span>{selectedFeedback?.createdAt?.format('ll')}</span>
                </p>
            </Modal>
        </>
    );
});
